import { beforeEach, describe, expect, it, vi } from 'vitest';
import { renderHook } from '@testing-library/react';
import { useWelcomeContent } from './use-welcome-content';

const mockFormatMessage = vi.fn(({ id }: { id: string }) => id);

vi.mock('react-intl', () => ({
  useIntl: () => ({
    formatMessage: mockFormatMessage,
  }),
}));

vi.mock('@repo/visual-assets/illustrations/welcome.png', () => ({
  default: 'welcome-illustration',
}));

vi.mock('@repo/visual-assets/illustrations/welcome-invoicing.png', () => ({
  default: 'welcome-invoicing-illustration',
}));

describe('useWelcomeContent', () => {
  beforeEach(() => {
    vi.clearAllMocks();
  });

  it.each([
    {
      legalFlow: 'qonto_invoicing',
      expectedTitle: 'qonto-pa.welcome.title',
      expectedSubtitle: 'qonto-pa.welcome.subtitle',
    },
    {
      legalFlow: 'existing_company',
      expectedTitle: 'welcome.new-user.title',
      expectedSubtitle: 'existing-company.fast-track.welcome-screen.subtitle',
    },
    {
      legalFlow: 'new_user',
      expectedTitle: 'welcome.new-user.title',
      expectedSubtitle: 'welcome.new-user.subtitle',
    },
  ])(
    'returns expected title and subtitle for $legalFlow',
    ({ legalFlow, expectedTitle, expectedSubtitle }) => {
      const { result } = renderHook(() =>
        useWelcomeContent({
          legalFlow,
          organizationName: 'ACME',
          ownerFirstName: 'Alice',
        })
      );

      expect(result.current.title).toBe(expectedTitle);
      expect(result.current.subtitle).toBe(expectedSubtitle);
    }
  );

  it('returns expected subtitle and specific content for legal place fast track', () => {
    const { result } = renderHook(() =>
      useWelcomeContent({
        legalFlow: 'existing_company',
        partnerName: 'legalplace',
        companyCreationFromScratch: false,
      })
    );

    expect(result.current.subtitle).toBe('welcome.new-user.legalplace.subtitle');
    expect(result.current.featuresList).toStrictEqual([
      'welcome.new-user.legalplace.list.item.1',
      'welcome.new-user.legalplace.list.item.2',
      'welcome.new-user.legalplace.list.item.3',
    ]);
  });

  it('returns expected subtitle and specific content for legal place fast track (Case Insensitive)', () => {
    const { result } = renderHook(() =>
      useWelcomeContent({
        legalFlow: 'existing_company',
        partnerName: 'LegalPlace',
        companyCreationFromScratch: false,
      })
    );

    expect(result.current.subtitle).toBe('welcome.new-user.legalplace.subtitle');
    expect(result.current.featuresList).toStrictEqual([
      'welcome.new-user.legalplace.list.item.1',
      'welcome.new-user.legalplace.list.item.2',
      'welcome.new-user.legalplace.list.item.3',
    ]);
  });

  it('does not returns different subtitle and specific content for legalPlace with company creation from scratch', () => {
    const { result } = renderHook(() =>
      useWelcomeContent({
        legalFlow: 'new_user',
        partnerName: 'legalplace',
        companyCreationFromScratch: true,
      })
    );

    expect(result.current.subtitle).toBe('welcome.new-user.subtitle');
    expect(result.current.featuresList).toStrictEqual([
      'welcome.new-user.list.item.1',
      'welcome.new-user.list.item.2',
      'welcome.new-user.list.item.3',
    ]);
  });

  it('returns embedded content when flow is embedded', () => {
    const { result } = renderHook(() =>
      useWelcomeContent({
        isEmbedded: true,
        legalFlow: 'existing_company',
        partnerName: 'Partner X',
      })
    );

    expect(result.current.subtitle).toBe('existing-company.qonto-embed.welcome-screen.subtitle');
    expect(result.current.featuresList).toStrictEqual([
      'existing-company.qonto-embed.welcome-screen.list.item.1',
      'existing-company.qonto-embed.welcome-screen.list.item.2',
      'existing-company.qonto-embed.welcome-screen.list.item.3',
    ]);
  });

  it('returns invoicing-specific content', () => {
    const { result } = renderHook(() =>
      useWelcomeContent({
        legalFlow: 'qonto_invoicing',
      })
    );

    expect(result.current.featuresList).toStrictEqual([
      'qonto-pa.welcome.features.e-invoicing',
      'qonto-pa.welcome.features.invoice-sync',
    ]);
    expect(result.current.illustrationSrc).toBe('welcome-invoicing-illustration');
    expect(result.current.ctaLabel).toBe('welcome.new-user.cta');
    expect(result.current.socialProofLabel).toBe('welcome.new-user.socialproof');
  });

  it('returns existing-company CTA and promo-banner flag', () => {
    const { result } = renderHook(() =>
      useWelcomeContent({
        legalFlow: 'existing_company',
        partnerName: 'Beglaubigt Partner',
      })
    );

    expect(result.current.ctaLabel).toBe('existing-company.fast-track.welcome-screen.cta');
    expect(result.current.shouldShowPromoBanner).toBe(true);
    expect(result.current.illustrationSrc).toBe('welcome-illustration');
  });

  it('returns accountant features for non-invoicing non-embedded flow', () => {
    const { result } = renderHook(() =>
      useWelcomeContent({
        isAccountant: true,
        legalFlow: 'new_user',
      })
    );

    expect(result.current.featuresList).toStrictEqual([
      'welcome.new-user.accountant.list.item.1',
      'welcome.new-user.accountant.list.item.2',
      'welcome.new-user.accountant.list.item.3',
    ]);
  });

  it('passes interpolation payloads to formatMessage', () => {
    renderHook(() =>
      useWelcomeContent({
        legalFlow: 'new_user',
        organizationName: 'ACME Corp',
        ownerFirstName: 'Alice',
      })
    );

    expect(mockFormatMessage).toHaveBeenCalledWith(
      { id: 'welcome.new-user.title' },
      { owner_first_name: 'Alice' }
    );
    expect(mockFormatMessage).toHaveBeenCalledWith(
      { id: 'welcome.new-user.subtitle' },
      { organization_name: 'ACME Corp' }
    );
  });
});
